import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, CircularProgress, IconButton, Tooltip, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api } from '../../api/api';

const PRIORITY = {
  '🔴': { label: '긴급', color: '#ef4444', bg: '#fef2f2', border: '#fca5a5' },
  '🟡': { label: '단기', color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
  '🟢': { label: '중기', color: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
  '📋': { label: '장기', color: '#6366f1', bg: '#f5f3ff', border: '#c4b5fd' },
};

function parseTodo(markdown) {
  const sections = [];
  let current = null;

  for (const raw of markdown.split('\n')) {
    const line = raw.trimEnd();

    // 섹션 헤더 (## 🔴 긴급 ...)
    const sectionMatch = line.match(/^## (.+)/);
    if (sectionMatch) {
      const title = sectionMatch[1].trim();
      const emoji = Object.keys(PRIORITY).find(e => title.startsWith(e)) || '📋';
      current = { title, emoji, priority: PRIORITY[emoji], items: [] };
      sections.push(current);
      continue;
    }

    if (!current) continue;

    // 체크 항목
    const itemMatch = line.match(/^- \[([ x])\] \*\*(.+?)\*\* — (.+)/);
    if (itemMatch) {
      current.items.push({
        done: itemMatch[1] === 'x',
        title: itemMatch[2],
        desc: itemMatch[3],
        sub: [],
      });
      continue;
    }

    // 서브 항목 (들여쓰기)
    const subMatch = line.match(/^  - (.+)/);
    if (subMatch && current.items.length > 0) {
      current.items[current.items.length - 1].sub.push(subMatch[1]);
    }
  }

  return sections.filter(s => s.items.length > 0);
}

export default function TodoSettings() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/todo');
      setSections(parseTodo(data.content || ''));
    } catch {
      setSections([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (sIdx, iIdx) => {
    const next = sections.map((s, si) =>
      si !== sIdx ? s : {
        ...s,
        items: s.items.map((item, ii) =>
          ii !== iIdx ? item : { ...item, done: !item.done }
        ),
      }
    );
    setSections(next);
    setSaving(true);
    try {
      await api.post('/admin/todo', { sections: next });
    } catch {}
    setSaving(false);
  };

  const total = sections.reduce((a, s) => a + s.items.length, 0);
  const done = sections.reduce((a, s) => a + s.items.filter(i => i.done).length, 0);

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;

  return (
    <Box>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>작업 체크리스트</Typography>
          <Chip
            size="small"
            label={`${done} / ${total} 완료`}
            sx={{
              fontSize: 11, fontWeight: 600,
              bgcolor: done === total && total > 0 ? '#d1fae5' : '#ede9fe',
              color: done === total && total > 0 ? '#065f46' : '#5b21b6',
            }}
          />
          {saving && <CircularProgress size={14} />}
        </Box>
        <Tooltip title="새로고침">
          <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {/* 진행 바 */}
      <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#e5e7eb', mb: 3, overflow: 'hidden' }}>
        <Box sx={{
          height: '100%',
          borderRadius: 3,
          width: total > 0 ? `${(done / total) * 100}%` : '0%',
          bgcolor: '#551FFF',
          transition: 'width 0.4s ease',
        }} />
      </Box>

      {sections.length === 0 ? (
        <Typography color="text.secondary" fontSize={14}>TODO.md 파일을 찾을 수 없습니다.</Typography>
      ) : (
        sections.map((section, si) => (
          <Box key={si} sx={{ mb: 3 }}>
            {/* 섹션 헤더 */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
              px: 1.5, py: 0.75,
              bgcolor: section.priority.bg,
              border: `1px solid ${section.priority.border}`,
              borderRadius: 2,
            }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: section.priority.color }}>
                {section.title}
              </Typography>
              <Chip
                size="small"
                label={`${section.items.filter(i => i.done).length}/${section.items.length}`}
                sx={{
                  fontSize: 10, height: 18, ml: 'auto',
                  bgcolor: 'rgba(0,0,0,0.06)',
                  color: section.priority.color,
                  fontWeight: 600,
                }}
              />
            </Box>

            {/* 항목 목록 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {section.items.map((item, ii) => (
                <Box
                  key={ii}
                  onClick={() => toggle(si, ii)}
                  sx={{
                    display: 'flex', gap: 1.5, alignItems: 'flex-start',
                    p: '10px 12px',
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: item.done ? '#d1fae5' : '#e5e7eb',
                    bgcolor: item.done ? '#f0fdf4' : '#fff',
                    opacity: item.done ? 0.7 : 1,
                    transition: 'all 0.18s',
                    '&:hover': { borderColor: '#551FFF', bgcolor: item.done ? '#f0fdf4' : '#faf8ff' },
                  }}
                >
                  <Box sx={{ mt: 0.2, flexShrink: 0 }}>
                    {item.done
                      ? <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                    }
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: 13, fontWeight: 600,
                      textDecoration: item.done ? 'line-through' : 'none',
                      color: item.done ? '#9ca3af' : '#111827',
                    }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.3, lineHeight: 1.5 }}>
                      {item.desc}
                    </Typography>
                    {item.sub.length > 0 && (
                      <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid #e5e7eb' }}>
                        {item.sub.map((s, si2) => (
                          <Typography key={si2} sx={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.6 }}>
                            · {s}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>

            {si < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))
      )}
    </Box>
  );
}
